import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Input, Typography, Select, Alert, message, notification } from "antd";
import RateApi from "../../common/Apis/RatesApi";
import { createDefaultTenantRate } from "../../../models/Rate/TenantRateDTO";

const { Title } = Typography;
const { Option } = Select;

const AddTenantRateModal = ({ show, onClose, onSubmit, tenant }) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (show) {
      form.resetFields();
      setIsButtonDisabled(true);
      setLoading(false);
      setApiError(null);
    }
  }, [show, form]);

  const handleFieldsChange = () => {
    validateRequiredFields();
  };

  const validateRequiredFields = () => {
    const requiredFields = ["paymentTerm", "rate"];
    const hasErrors = form
      .getFieldsError(requiredFields)
      .some(({ errors }) => errors.length > 0);
    const allTouched = requiredFields.every((field) =>
      form.isFieldTouched(field)
    );
    setIsButtonDisabled(!allTouched || hasErrors);
  };

  const handleAdd = async () => {
    form
      .validateFields(["paymentTerm", "rate"])
      .then((values) => {
        if (!tenant?.id) {
          return message.error("Tenant ID is missing.");
        }

        const payload = createDefaultTenantRate({
          tenantDTO: { id: tenant?.id },
          billingCycle: values.paymentTerm,
          agreedRate: values.rate,
        });

        setLoading(true);
        RateApi.AddRateForTenant(payload) 
          .then(() => {
            setApiError(null);
            notification.open({
              message: (
                <span style={{ fontWeight: "bold" }}>
                  Tenant / Customer subscription plan added successfully!
                </span>
              ),
              closeIcon: false,
              duration: 3,
              style: {
                backgroundColor: "#b3ffb3",
                color: "green",
              },
            });
            setLoading(false);
            onSubmit?.(); // Refresh parent list
            onClose();
          })
          .catch((err) => {
            setApiError(
              err?.apiError?.message ||
                err?.message ||
                "Failed to add subscription plan. Please try again."
            );
            setLoading(false);
          });
      })
      .catch(() => {
        message.error("Please fix the form errors before proceeding.");
      });
  };

  return (
    <Modal
      footer={null}
      width={650}
      open={show}
      maskClosable={false}
      onCancel={onClose}
      closable={true}
    >
      <Title level={4} className="text-center mb-3">
        Add Tenant / Customer subscription plan
      </Title>

      <Form
        form={form}
        layout="vertical"
        name="add_tenant_rate_form"
        onFieldsChange={handleFieldsChange}
      >
        <Form.Item
          name="paymentTerm"
          label={
            <span>
              Payment Term <span style={{ color: "red" }}>*</span>
            </span>
          }
          rules={[{ required: true, message: "Please select payment term" }]}
        >
          <Select placeholder="Select Payment Term">
            <Option value="MONTHLY">Monthly</Option>
            <Option value="ANNUALLY">Annual</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label={
            <span>
              Rate <span style={{ color: "red" }}>*</span>
            </span>
          }
          name="rate"
          rules={[
            { required: true, message: "Please enter rate" },
            {
              validator(_, value) {
                if (isNaN(value)) {
                  return Promise.reject("Rate must be a number");
                }
                if (Number(value) < 0) {
                  return Promise.reject("Rate cannot be negative");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="Enter rate" />
        </Form.Item>

        {apiError && (
          <Alert message={apiError} type="error" showIcon className="mb-3" />
        )}

        <Button
          type="primary"
          disabled={isButtonDisabled}
          loading={loading}
          style={{ width: "100%" }}
          onClick={handleAdd}
        >
          Add subscription plan
        </Button>
      </Form>
    </Modal>
  );
};

export default AddTenantRateModal;
